import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";
import AdminHeader from "components/Headers/AdminHeader";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { DEFAULT_RECORDS_PER_PAGE } from "components/constant";
import { getChannelPartnerCourseEnrollmentReport } from "action/channelPartner";
import { courseEnrollmentReport } from "constants/report";

const initialFilters = {
  course: "",
  batch: "",
};

const ChannelPartnerCourseEnrollmentReport = () => {
  const dispatch = useDispatch();
  const courseEnrollmentReportData = useSelector(
    (state) => state.report.courseEnrollmentReport,
  );

  const [filters, setFilters] = useState(initialFilters);
  const [filterState, setFilterState] = useState({ filterParams: [] });
  const [pageNum, setPageNum] = useState(1);
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });

  useEffect(() => {
    getChannelPartnerCourseEnrollmentReport(
      dispatch,
      pageNum,
      filterState.filterParams,
    );

    return () => {
      dispatch({ type: courseEnrollmentReport, payload: {} });
    };
  }, [dispatch, pageNum, filterState.filterParams]);

  useEffect(() => {
    if (courseEnrollmentReportData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords:
          courseEnrollmentReportData?.data?.channelPartnerCourseEnrollmentReport
            ?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (
      courseEnrollmentReportData?.code ||
      courseEnrollmentReportData?.message
    ) {
      setListState({
        isLoading: false,
        error:
          courseEnrollmentReportData?.message ||
          "Failed to load course enrollment report.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [courseEnrollmentReportData]);

  const buildFilterParams = () => {
    const params = [];

    if (filters.course?.trim()) {
      params.push({
        field: "CourseName",
        condition: "like",
        filterValues: [filters.course.trim()],
      });
    }

    if (filters.batch?.trim()) {
      params.push({
        field: "BatchName",
        condition: "like",
        filterValues: [filters.batch.trim()],
      });
    }

    return params.length > 0 ? JSON.stringify(params) : [];
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    setFilters((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(filterState.filterParams) !==
      JSON.stringify(newFilterParams)
    ) {
      setListState((previous) => ({
        ...previous,
        isLoading: true,
        error: "",
        showLoaderOnClick: true,
      }));

      setFilterState({ filterParams: newFilterParams });

      if (pageNum !== 1) {
        setPageNum(1);
      }
    }
  };

  const handleClear = () => {
    setListState((previous) => ({
      ...previous,
      isLoading: true,
      error: "",
      showLoaderOnClick: false,
    }));

    setFilters(initialFilters);
    setFilterState({ filterParams: [] });

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      minWidth: 70,
      maxWidth: 80,
      centerAlign: true,
    },
    {
      Header: "Course",
      accessor: "courseName",
      minWidth: 260,
      maxWidth: 260,
      Cell: ({ value }) => <div className="text-wrapper">{value || "-"}</div>,
    },
    {
      Header: "Batch",
      accessor: "batchName",
      minWidth: 220,
      maxWidth: 220,
      Cell: ({ value }) => <div className="text-wrapper">{value || "-"}</div>,
    },
    {
      Header: "Enrolled Trainees",
      accessor: "totalEnrolledTrainees",
      minWidth: 160,
      maxWidth: 160,
      centerAlign: true,
    },
  ];

  const tableData = (
    courseEnrollmentReportData?.data?.channelPartnerCourseEnrollmentReport || []
  ).map((item, index) => ({
    ...item,
    srNo:
      (pageNum - 1) *
        (courseEnrollmentReportData?.data?.noOfRecordsPerPage ||
          DEFAULT_RECORDS_PER_PAGE) +
      index +
      1,
    courseName: item?.courseName || "-",
    batchName: item?.batchName || "-",
    totalEnrolledTrainees: item?.totalEnrolledTrainees ?? 0,
  }));

  return (
    <>
      <AdminHeader />
      <Container fluid className="mt--6">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between">
                <CardTitle tag="h3" className="mb-0 text-default">
                  Course Enrollment Report
                </CardTitle>
              </CardHeader>

              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      name="course"
                      placeholder="Search by course"
                      className="mr-2 mb-2 filter-input-large"
                      value={filters.course}
                      onChange={handleFilterChange}
                      onKeyDown={handleKeyDown}
                    />

                    <Input
                      type="text"
                      name="batch"
                      placeholder="Search by batch"
                      className="mr-2 mb-2 filter-input-large"
                      value={filters.batch}
                      onChange={handleFilterChange}
                      onKeyDown={handleKeyDown}
                    />

                    <Button
                      color="default"
                      onClick={handleSearch}
                      disabled={listState.isLoading}
                      className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${
                        listState.showLoaderOnClick ? "search-btn" : ""
                      }`}
                    >
                      {listState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {filterState.filterParams.length > 0 && (
                      <Button
                        color="default"
                        className="mb-2 filter-btn btn-simple btn-deep-blue"
                        onClick={handleClear}
                      >
                        <i className="fa fa-times mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-2 mb-3 mx-1">
                  {courseEnrollmentReportData?.data?.totalRecords > 0 && (
                    <>
                      {filterState.filterParams.length > 0 ? (
                        <span className="total-records">
                          Showing{" "}
                          {
                            courseEnrollmentReportData?.data
                              ?.totalFilteredRecords
                          }{" "}
                          of {courseEnrollmentReportData?.data?.totalRecords}{" "}
                          Records
                        </span>
                      ) : (
                        <span className="total-records">
                          Total Records :{" "}
                          {courseEnrollmentReportData?.data?.totalRecords}
                        </span>
                      )}
                    </>
                  )}
                </div>

                {listState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : listState.error ? (
                  <div className="loaderContainer error-style">
                    <div className="error-icon">
                      <i className="fa-solid fa-circle-exclamation" />
                    </div>
                    <span>{listState.error}</span>
                  </div>
                ) : listState.noRecords || tableData.length === 0 ? (
                  <div className="loaderContainer empty-style">
                    <div className="empty-icon">
                      <i className="fa-solid fa-circle-info" />
                    </div>
                    <span>No enrollment records found.</span>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CommonTable
                      columns={columns}
                      data={tableData || []}
                      defaultPageSize={
                        courseEnrollmentReportData?.data?.noOfRecordsPerPage ||
                        DEFAULT_RECORDS_PER_PAGE
                      }
                      activePage={pageNum}
                      setPageNum={setPageNum}
                      totalPages={Math.ceil(
                        courseEnrollmentReportData?.data?.totalFilteredRecords /
                          (courseEnrollmentReportData?.data
                            ?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE) ||
                          DEFAULT_RECORDS_PER_PAGE,
                      )}
                      showPaginationBottom={true}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default ChannelPartnerCourseEnrollmentReport;
